import { useState, useEffect } from 'react';
import { Bell, Mail, Plus, X, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface NotificationSetting {
  id: string;
  notification_type: string;
  emails: string[];
  enabled: boolean;
}

const notificationTypeLabels: Record<string, { label: string; description: string }> = {
  new_order: {
    label: 'Nuevo Pedido',
    description: 'Notificar cuando se crea un nuevo pedido (cualquier metodo de pago)'
  },
  transfer_uploaded: {
    label: 'Comprobante de Transferencia',
    description: 'Notificar cuando un cliente sube un comprobante de transferencia'
  },
  card_payment_success: {
    label: 'Pago con Tarjeta Exitoso',
    description: 'Notificar cuando un pago con tarjeta es procesado exitosamente'
  }
};

export default function NotificationsTab() {
  const [settings, setSettings] = useState<NotificationSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newEmails, setNewEmails] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('notification_settings')
        .select('*')
        .order('notification_type');

      if (fetchError) throw fetchError;
      setSettings(data || []);
    } catch (err) {
      console.error('Error fetching notification settings:', err);
      setError('Error al cargar la configuracion de notificaciones');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = async (settingId: string, notificationType: string) => {
    const email = newEmails[notificationType]?.trim();
    if (!email) return;

    if (!validateEmail(email)) {
      setError('Por favor ingresa un email valido');
      return;
    }

    const setting = settings.find(s => s.id === settingId);
    if (!setting) return;

    if (setting.emails.includes(email)) {
      setError('Este email ya esta agregado');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updatedEmails = [...setting.emails, email];
      const { error: updateError } = await supabase
        .from('notification_settings')
        .update({ emails: updatedEmails, updated_at: new Date().toISOString() })
        .eq('id', settingId);

      if (updateError) throw updateError;

      setSettings(prev => prev.map(s =>
        s.id === settingId ? { ...s, emails: updatedEmails } : s
      ));
      setNewEmails(prev => ({ ...prev, [notificationType]: '' }));
      setSuccess('Email agregado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error adding email:', err);
      setError('Error al agregar el email');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveEmail = async (settingId: string, emailToRemove: string) => {
    const setting = settings.find(s => s.id === settingId);
    if (!setting) return;

    setIsSaving(true);
    setError(null);

    try {
      const updatedEmails = setting.emails.filter(e => e !== emailToRemove);
      const { error: updateError } = await supabase
        .from('notification_settings')
        .update({ emails: updatedEmails, updated_at: new Date().toISOString() })
        .eq('id', settingId);

      if (updateError) throw updateError;

      setSettings(prev => prev.map(s =>
        s.id === settingId ? { ...s, emails: updatedEmails } : s
      ));
      setSuccess('Email eliminado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing email:', err);
      setError('Error al eliminar el email');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = async (settingId: string, currentEnabled: boolean) => {
    setIsSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('notification_settings')
        .update({ enabled: !currentEnabled, updated_at: new Date().toISOString() })
        .eq('id', settingId);

      if (updateError) throw updateError;

      setSettings(prev => prev.map(s =>
        s.id === settingId ? { ...s, enabled: !currentEnabled } : s
      ));
    } catch (err) {
      console.error('Error toggling notification:', err);
      setError('Error al cambiar el estado de la notificacion');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-white rounded-xl shadow-sm">
        <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-600">Cargando configuracion...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notificaciones</h1>
            <p className="text-sm text-gray-500">Configura los emails que reciben notificaciones de pedidos</p>
          </div>
        </div>
        <button
          onClick={fetchSettings}
          className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Actualizar</span>
        </button>
      </div>

      {/* Mensajes de error/exito */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <Check className="w-5 h-5 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      {/* Lista de configuraciones */}
      <div className="space-y-4">
        {settings.map((setting) => {
          const typeInfo = notificationTypeLabels[setting.notification_type] || {
            label: setting.notification_type,
            description: ''
          };

          return (
            <div
              key={setting.id}
              className={`bg-white rounded-xl shadow-sm border-2 transition-colors ${
                setting.enabled ? 'border-transparent' : 'border-gray-200 opacity-75'
              }`}
            >
              <div className="p-4 sm:p-6">
                {/* Header de la notificacion */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${setting.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Mail className={`w-5 h-5 ${setting.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{typeInfo.label}</h3>
                      <p className="text-sm text-gray-500">{typeInfo.description}</p>
                    </div>
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggleEnabled(setting.id, setting.enabled)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      setting.enabled ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        setting.enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Lista de emails */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">
                    Emails destinatarios ({setting.emails.length})
                  </p>

                  {setting.emails.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {setting.emails.map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {email}
                          <button
                            onClick={() => handleRemoveEmail(setting.id, email)}
                            disabled={isSaving}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">
                      No hay emails configurados. Agrega uno para recibir notificaciones.
                    </p>
                  )}

                  {/* Input para agregar email */}
                  <div className="flex gap-2 mt-3">
                    <input
                      type="email"
                      placeholder="ejemplo@correo.com"
                      value={newEmails[setting.notification_type] || ''}
                      onChange={(e) => setNewEmails(prev => ({
                        ...prev,
                        [setting.notification_type]: e.target.value
                      }))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddEmail(setting.id, setting.notification_type);
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={() => handleAddEmail(setting.id, setting.notification_type)}
                      disabled={isSaving || !newEmails[setting.notification_type]?.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">Agregar</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Bell className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Acerca de las notificaciones</h4>
            <p className="text-sm text-blue-700 mt-1">
              Los emails configurados recibiran notificaciones automaticas cuando ocurran los eventos seleccionados.
              Puedes agregar multiples emails para cada tipo de notificacion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
